import { motion } from "framer-motion";

/**
 * CastleGraphic — the central visual metaphor for "Why Social Week".
 *
 * `layer` controls progressive disclosure:
 *   0 — empty stage (just hills + sky)
 *   1 — castle visible (core services)
 *   2 — castle + warriors (Social Go + Social Pro)
 *   3 — castle + warriors + moat (Social Week)
 *   4 — full + ambient gold glow (climax)
 *
 * Reused across Scenes 49–53. Each scene increments `layer`, so the same
 * figure stays on stage and additional elements fade in — like an architect's
 * diagram building up beat by beat.
 */

const STONE = "#0a0f0c";
const STONE_FACE = "#13211b";
const STONE_HIGHLIGHT = "#1d2e26";
const GOLD = "#e9a820";
const GOLD_DEEP = "#d4940a";
const GOLD_LIGHT = "#ffe8a8";
const AQUA = "#00deb6";
const LIME = "#96C11F";

// ── building blocks ────────────────────────────────────────────────────────

function CrenellatedWall({ x, width, top, bottom, notchSize = 14, fill = STONE }) {
  const numNotches = Math.floor(width / (notchSize * 2));
  return (
    <g>
      <rect x={x} y={top} width={width} height={bottom - top} fill={fill} />
      {Array.from({ length: numNotches }).map((_, i) => (
        <rect
          key={i}
          x={x + i * notchSize * 2}
          y={top - notchSize}
          width={notchSize}
          height={notchSize}
          fill={fill}
        />
      ))}
    </g>
  );
}

function Tower({ x, y, width, height, fill = STONE, withRoof = true, roofHeight = 80 }) {
  const cx = x + width / 2;
  return (
    <g>
      <CrenellatedWall x={x} width={width} top={y} bottom={y + height} fill={fill} />
      {withRoof && (
        <polygon
          points={`${x - 8},${y - 14} ${x + width + 8},${y - 14} ${cx},${y - roofHeight}`}
          fill={fill}
        />
      )}
      {/* Tower window slit (warm interior glow when castle is on) */}
      <rect
        x={cx - 3}
        y={y + height / 2 - 16}
        width="6"
        height="28"
        fill={GOLD_LIGHT}
        opacity="0.22"
      />
    </g>
  );
}

function Banner({ x, y, height = 28, color = GOLD, delay = 0 }) {
  // pole tip is at (x, y - height); banner waves to the right.
  const baseX = x + 1.5;
  const baseY = y - height;
  const tipY = y - height + 24;
  const flag1 = `M ${baseX},${baseY} L ${baseX + 32},${baseY + 4} L ${baseX + 28},${baseY + 14} L ${baseX + 32},${baseY + 24} L ${baseX},${tipY} Z`;
  const flag2 = `M ${baseX},${baseY} L ${baseX + 28},${baseY + 6} L ${baseX + 32},${baseY + 14} L ${baseX + 28},${baseY + 22} L ${baseX},${tipY} Z`;
  return (
    <g>
      <rect x={x - 1} y={y - height} width="2" height={height + 1} fill={STONE_FACE} />
      <motion.path
        d={flag1}
        fill={color}
        animate={{ d: [flag1, flag2, flag1] }}
        transition={{ duration: 2.6, repeat: Infinity, delay, ease: "easeInOut" }}
      />
    </g>
  );
}

function Archer({ x, y, delay = 0 }) {
  return (
    <motion.g
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.45, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* warm halo */}
      <circle cx={x} cy={y - 8} r="14" fill={GOLD} opacity="0.16" filter="url(#cg-glow)" />
      {/* Body */}
      <rect x={x - 2.4} y={y - 14} width="4.8" height="14" fill={GOLD} rx="0.8" />
      {/* Head */}
      <circle cx={x} cy={y - 18} r="2.8" fill={GOLD} />
      {/* Bow */}
      <path
        d={`M ${x + 4.5},${y - 16} Q ${x + 11},${y - 8} ${x + 4.5},${y}`}
        stroke={GOLD}
        strokeWidth="1.3"
        fill="none"
        strokeLinecap="round"
      />
    </motion.g>
  );
}

function Knight({ x, y, delay = 0 }) {
  return (
    <motion.g
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      <circle cx={x} cy={y - 14} r="22" fill={GOLD} opacity="0.18" filter="url(#cg-glow)" />
      {/* Body */}
      <rect x={x - 5} y={y - 22} width="10" height="22" fill={GOLD} rx="1" />
      {/* Head */}
      <circle cx={x} cy={y - 26} r="4.5" fill={GOLD} />
      {/* Sword (vertical) */}
      <rect x={x + 7} y={y - 32} width="2.4" height="32" fill={GOLD} />
      <rect x={x + 4} y={y - 30} width="8" height="2" fill={GOLD} />
      {/* Shield */}
      <ellipse cx={x - 8} cy={y - 14} rx="4" ry="6" fill={GOLD_DEEP} />
    </motion.g>
  );
}

// ── main component ─────────────────────────────────────────────────────────

export default function CastleGraphic({ layer = 0, glow = false, className = "", moatTone = "aqua" }) {
  const moatGold = moatTone === "gold";
  const moatStop1 = moatGold ? "rgba(233,168,32,0.55)" : "rgba(0,222,182,0.55)";
  const moatStop2 = moatGold ? "rgba(212,148,10,0.40)" : "rgba(150,193,31,0.40)";
  const moatStop3 = moatGold ? "rgba(233,168,32,0.16)" : "rgba(0,222,182,0.16)";
  const moatGlowStop1 = moatGold ? "rgba(233,168,32,0.40)" : "rgba(0,222,182,0.40)";
  const moatGlowStop2 = moatGold ? "rgba(233,168,32,0)" : "rgba(0,222,182,0)";
  const moatStroke = moatGold ? "rgba(255,232,168,0.85)" : "rgba(0,222,182,0.75)";
  const wave2Color = moatGold ? "rgba(255,232,168,0.85)" : "rgba(0,222,182,0.85)";
  return (
    <svg
      viewBox="0 0 1200 720"
      className={`w-full h-full ${className}`}
      preserveAspectRatio="xMidYMid meet"
      data-testid={`castle-graphic-layer-${layer}`}
    >
      <defs>
        <filter id="cg-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
        <filter id="cg-strong-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="14" />
        </filter>
        <linearGradient id="cg-moat" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={moatStop1} />
          <stop offset="50%" stopColor={moatStop2} />
          <stop offset="100%" stopColor={moatStop3} />
        </linearGradient>
        <radialGradient id="cg-moat-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor={moatGlowStop1} />
          <stop offset="100%" stopColor={moatGlowStop2} />
        </radialGradient>
        <radialGradient id="cg-aura" cx="0.5" cy="0.55" r="0.6">
          <stop offset="0%" stopColor="rgba(255,232,168,0.18)" />
          <stop offset="100%" stopColor="rgba(255,232,168,0)" />
        </radialGradient>
      </defs>

      {/* Climax ambient aura */}
      {glow && (
        <motion.ellipse
          cx="600"
          cy="420"
          rx="600"
          ry="320"
          fill="url(#cg-aura)"
          animate={{ opacity: [0.45, 0.85, 0.45] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
      )}

      {/* Distant hill silhouette */}
      <path
        d="M 0,640 Q 250,580 500,620 Q 750,650 1000,600 Q 1140,575 1200,605 L 1200,720 L 0,720 Z"
        fill="rgba(20,32,25,0.55)"
      />
      {/* Foreground hill */}
      <path
        d="M 0,690 Q 300,660 600,680 Q 900,695 1200,665 L 1200,720 L 0,720 Z"
        fill="rgba(8,14,11,0.92)"
      />

      {/* MOAT — layer 3 */}
      {layer >= 3 && (
        <motion.g
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Outer glow halo */}
          <ellipse cx="600" cy="660" rx="610" ry="56" fill="url(#cg-moat-glow)" />

          {/* Moat body — animates a gentle vertical breathing */}
          <motion.ellipse
            cx="600"
            cy="650"
            rx="560"
            ry="40"
            fill="url(#cg-moat)"
            stroke={moatStroke}
            strokeWidth="1.5"
            animate={{ ry: [40, 42, 40] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* Inner highlight ring */}
          <ellipse
            cx="600"
            cy="650"
            rx="540"
            ry="32"
            fill="none"
            stroke="rgba(255,232,168,0.32)"
            strokeWidth="1"
          />

          {/* Three layered wave bands sliding horizontally — water in motion */}
          {[
            { y: 642, amp: 4, len: 80, dur: 7.0, op: 0.55, color: "rgba(255,232,168,0.65)", w: 1.6 },
            { y: 650, amp: 3, len: 64, dur: 5.4, op: 0.45, color: wave2Color, w: 1.2 },
            { y: 658, amp: 5, len: 96, dur: 9.0, op: 0.35, color: "rgba(255,255,255,0.55)", w: 1.0 },
          ].map((wave, i) => {
            // Build an extended wave path — wider than the SVG so we can slide it left.
            const segs = 30;
            let d = `M 40 ${wave.y}`;
            for (let s = 0; s < segs; s++) {
              const x = 40 + s * wave.len;
              const dy = s % 2 === 0 ? -wave.amp : wave.amp;
              d += ` q ${wave.len / 2} ${dy} ${wave.len} 0`;
            }
            return (
              <motion.path
                key={`wave-${i}`}
                d={d}
                stroke={wave.color}
                strokeWidth={wave.w}
                fill="none"
                strokeLinecap="round"
                opacity={wave.op}
                animate={{ x: [0, -wave.len * 2] }}
                transition={{
                  duration: wave.dur,
                  repeat: Infinity,
                  ease: "linear",
                  delay: i * 0.4,
                }}
                style={{ filter: "blur(0.4px)" }}
              />
            );
          })}

          {/* Sparkle shimmer dots — twinkle randomly across the surface */}
          {[
            [180, 644], [260, 658], [340, 648], [430, 642], [520, 656],
            [600, 644], [680, 658], [770, 648], [860, 642], [940, 656], [1020, 648],
          ].map(([cx, cy], i) => (
            <motion.circle
              key={`sparkle-${i}`}
              cx={cx}
              cy={cy}
              r={1.4}
              fill="rgba(255,255,255,0.95)"
              animate={{ opacity: [0, 1, 0] }}
              transition={{
                duration: 1.6,
                repeat: Infinity,
                delay: (i * 0.27) % 3,
                ease: "easeInOut",
              }}
            />
          ))}
        </motion.g>
      )}

      {/* CASTLE — layer 1+ */}
      {layer >= 1 && (
        <motion.g
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Castle base shadow on ground */}
          <ellipse cx="600" cy="640" rx="380" ry="20" fill="#000" opacity="0.55" />

          {/* Connecting walls */}
          <CrenellatedWall x={360} width={150} top={440} bottom={620} fill={STONE_FACE} />
          <CrenellatedWall x={690} width={150} top={440} bottom={620} fill={STONE_FACE} />

          {/* Side towers */}
          <Tower x={280} y={350} width={80} height={270} fill={STONE_FACE} roofHeight={75} />
          <Tower x={840} y={350} width={80} height={270} fill={STONE_FACE} roofHeight={75} />

          {/* Center keep */}
          <Tower x={510} y={240} width={180} height={380} fill={STONE} roofHeight={100} />

          {/* Subtle face highlight on left side of keep (depth) */}
          <rect x="510" y="240" width="6" height="380" fill={STONE_HIGHLIGHT} opacity="0.6" />

          {/* Gate */}
          <path
            d="M 565,620 L 565,510 Q 565,470 600,470 Q 635,470 635,510 L 635,620 Z"
            fill="#000"
          />
          {/* Gate trim */}
          <path
            d="M 562,510 Q 562,468 600,468 Q 638,468 638,510"
            stroke={GOLD_DEEP}
            strokeWidth="2.5"
            fill="none"
            opacity="0.55"
          />
          {/* Gate warm glow */}
          <ellipse cx="600" cy="540" rx="38" ry="50" fill={GOLD} opacity="0.18" filter="url(#cg-glow)" />

          {/* Banners */}
          <Banner x={320} y={276} height={28} delay={0.3} />
          <Banner x={880} y={276} height={28} delay={0.6} />
          <Banner x={600} y={142} height={36} color={GOLD} delay={0.45} />
        </motion.g>
      )}

      {/* WARRIORS — layer 2+ */}
      {layer >= 2 && (
        <g>
          {/* Archers on left tower battlement (top of tower at y=336) */}
          <Archer x={300} y={336} delay={0.15} />
          <Archer x={335} y={336} delay={0.25} />
          {/* Archers on right tower battlement */}
          <Archer x={860} y={336} delay={0.35} />
          <Archer x={895} y={336} delay={0.45} />
          {/* Archers on center keep top (y=226) */}
          <Archer x={550} y={226} delay={0.55} />
          <Archer x={600} y={226} delay={0.65} />
          <Archer x={650} y={226} delay={0.75} />
          {/* Archers on left wall (top y=426) */}
          <Archer x={400} y={426} delay={0.85} />
          <Archer x={460} y={426} delay={0.95} />
          {/* Archers on right wall */}
          <Archer x={730} y={426} delay={1.05} />
          <Archer x={790} y={426} delay={1.15} />
          {/* Knights guarding gate (in front, ground y=620) */}
          <Knight x={500} y={620} delay={1.25} />
          <Knight x={700} y={620} delay={1.35} />
        </g>
      )}
    </svg>
  );
}
